import React, { useState, useEffect } from 'react';
import { userService } from '../../services/userService';
import UserCreateForm from './UserCreateForm';
import UserEditForm from './UserEditForm';
import Modal from '../Modal';
import './Users.css';

const UsersModal = ({ isOpen, onClose, currentUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  useEffect(() => {
    if (isOpen) {
      loadUsers();
    }
  }, [isOpen]);

  const loadUsers = async () => {
    setLoading(true);
    const result = await userService.getUsers();

    if (result.success) {
      setUsers(result.users);
    } else {
      showNotification(result.error, 'error');
    }
    setLoading(false);
  };

  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
  };

  const handleDeleteUser = async (user) => {
    if (user.id === currentUser.id) {
      showNotification('Cannot delete your own account', 'error');
      return;
    }

    if (window.confirm(`Are you sure you want to delete user ${user.email}?`)) {
      const result = await userService.deleteUser(user.id);

      if (result.success) {
        showNotification('User deleted successfully', 'success');
        loadUsers(); // Refresh the list
      } else {
        showNotification(result.error, 'error');
      }
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowEditForm(true);
  };

  const handleUserCreated = () => {
    setShowCreateForm(false);
    loadUsers();
    showNotification('User created successfully', 'success');
  };

  const handleUserUpdated = () => {
    setShowEditForm(false);
    setSelectedUser(null);
    loadUsers();
    showNotification('User updated successfully', 'success');
  };

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleBadgeClass = (role) => {
    return role === 'superuser' ? 'badge badge-admin' : 'badge badge-user';
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal users-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Users Management</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {notification.show && (
            <div className={`notification ${notification.type}`}>
              {notification.message}
            </div>
          )}

          <div className="users-controls">
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search users by email or role..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <button
              className="btn btn-primary"
              onClick={() => setShowCreateForm(true)}
            >
              Create New User
            </button>
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading users...</p>
            </div>
          ) : (
            <div className="users-table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id}>
                      <td>{user.email}</td>
                      <td>
                        <span className={getRoleBadgeClass(user.role)}>
                          {user.role}
                        </span>
                      </td>
                      <td>
                        <span className={`status ${user.is_active ? 'active' : 'inactive'}`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>{formatDate(user.created_at)}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn btn-small btn-secondary"
                            onClick={() => handleEditUser(user)}
                            title="Edit user"
                          >
                            ✏️
                          </button>
                          <button
                            className="btn btn-small btn-danger"
                            onClick={() => handleDeleteUser(user)}
                            disabled={user.id === currentUser.id}
                            title={user.id === currentUser.id ? 'Cannot delete yourself' : 'Delete user'}
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredUsers.length === 0 && !loading && (
                <div className="empty-state">
                  <p>No users found matching your search criteria.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateForm && (
        <UserCreateForm
          isOpen={showCreateForm}
          onClose={() => setShowCreateForm(false)}
          onUserCreated={handleUserCreated}
        />
      )}

      {/* Edit User Modal */}
      {showEditForm && selectedUser && (
        <UserEditForm
          isOpen={showEditForm}
          onClose={() => {
            setShowEditForm(false);
            setSelectedUser(null);
          }}
          user={selectedUser}
          currentUser={currentUser}
          onUserUpdated={handleUserUpdated}
        />
      )}
    </div>
  );
};

export default UsersModal;